import React, {
  useState,
  useRef,
  useContext,
  useEffect,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid } from "@mui/material";
import { formStateHandler } from "../../../../utils/utils";
import TextInput from "../../../../framework/TextInput";
import SelectInput from "../../../../framework/SelectInput";
import NumberInput from "../../../../framework/NumberInput";
import Switch from "../../../../framework/Switch";
import GlobalContext from "../../../../framework/GlobalContext";
import { openingTransactionTypes } from "../../../../utils/constant";
import _ from "lodash";
import { listBanks } from "../../../../services/bank-services";

const AccountTab = () => {
  const [openingTransactionType, setOpeningTransactionType] = useState(
    openingTransactionTypes[0]
  );
  const [localFormState, setLocalFormState] = useState(null);
  const addDistributorOpeningTransactionTypeRef = useRef();
  const addDistributorBankRef = useRef();
  const addDistributorIfscRef = useRef();
  const addDistributorMicrRef = useRef();
  const addDistributorTcsPercentRef = useRef();
  const addDistributorPayableFromAdminRef = useRef();
  const addDistributorChqPrintEnableRef = useRef();
  const addDistributorPayeeNameRef = useRef();
  const addDistributorAccountNoRef = useRef();
  const addDistributorOpeningBalanceRef = useRef();
  const addDistributorCreditAmountRef = useRef();
  const addDistributorCreditPeriodRef = useRef();
  const addDistributorPanTanRef = useRef();
  const [bankList, setBankList] = useState([]);
  const { formState } = useSelector(
    (state) => state.commonReducer.common.addDistributor
  );
  const dispatch = useDispatch();
  const { setCurrentComponent } = useContext(GlobalContext);
  const dispatchFormState = useCallback(
    (newState) => {
      dispatch({
        type: "set_add_distributor_form_state",
        payload: newState,
      });
    },
    [dispatch]
  );
  const debounceDispatchFormState = _.debounce(dispatchFormState, 1);
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options) => {
      let newFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options
      );
      debounceDispatchFormState(newFormState);
    },
    [debounceDispatchFormState, formState]
  );
  const initializeForm = useCallback((formState) => {
    if (formState) {
      addDistributorPayeeNameRef.current.value = formState.chqName || "";
      addDistributorBankRef.current.value = formState?.bankName || "";
      addDistributorAccountNoRef.current.value = formState?.accountNumber || "";
      addDistributorIfscRef.current.value = formState?.ifsc || "";
      addDistributorMicrRef.current.value = formState?.micrCode || "";

      if (formState.openingBalance !== null) {
        setOpeningTransactionType((prev) => {
          if (formState.openingBalance > 0) {
            setOpeningTransactionType(openingTransactionTypes[0]);
          } else {
            setOpeningTransactionType(openingTransactionTypes[1]);
          }
        });
      }
    }
  }, []);
  useEffect(() => {
    addDistributorPayeeNameRef.current.focus({ preventScroll: true });
  }, [addDistributorPayeeNameRef]);
  useEffect(() => {
    if (formState && !localFormState) {
      setLocalFormState(formState);
      initializeForm(formState);
    }
  }, [localFormState, initializeForm, formState]);
  useEffect(() => {
    setTimeout(() => {
      fetchBanks();
    }, 100);
  }, []);
  const fetchBanks = async () => {
    try {
      const query = {};
      const response = await listBanks(query);
      setBankList(response.rows);
      if (Array.isArray(response?.rows) && response.rows.length > 0) {
        const formattedBanks = response.rows;
        setBankList(formattedBanks);
      } else {
        console.warn("No valid rows found in response.");
        setBankList([]);
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
      setBankList([]);
    }
  };
  useEffect(() => {
    if (formState && !localFormState) {
      setLocalFormState(formState);
      initializeForm(formState);
    }
  }, [localFormState, initializeForm, formState]);

  const handleBankSelection = useCallback(
    (e, option) => {
      addDistributorAccountNoRef.current.value = option?.accountNo || "";
      addDistributorIfscRef.current.value = option?.ifsc || "";
      addDistributorMicrRef.current.value = option?.micrCode || "";
      setLocalFormState((prevState) => ({
        ...prevState,
        accountNumber: option?.accountNo || "",
        ifsc: option?.ifsc || "",
        micrCode: option?.micrCode || "",
        bankName: option?.bankName || "",
      }));
      const newState = {
        ...formState,
        accountNumber: option?.accountNo || "",
        ifsc: option?.ifsc || "",
        micrCode: option?.micrCode || "",
        bankName: option?.bankName || "",
      };
      dispatchFormState(newState);
    },
    [dispatchFormState, formState]
  );

  return (
    <Grid
      sx={{
        margin: "5px 0px 5px -10px",
        width: "100%",
      }}
      container
      columnSpacing={2}
      rowSpacing={2.5}
      columns={12}
    >
      <Grid item xs={3}>
        <TextInput
          id="add_distributor_payee_name"
          name="chqName"
          ref={addDistributorPayeeNameRef}
          label="Cheque Name or Payee Name"
          onFocus={() => setCurrentComponent("add_distributor_payee_name")}
          onChange={(e) => {
            const sanitizedChqName = e.target.value.replace(
              /[^a-zA-Z0-9\s-]/g,
              ""
            );
            handleFormChange("chqName", sanitizedChqName);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_payee_name",
              down: "add_distributor_tcs_applied",
              left: "add_distributor_payee_name",
              right: "add_distributor_bank",
            }),
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <SelectInput
          label="Bank"
          id="add_distributor_bank"
          ref={addDistributorBankRef}
          placeHolder="Bank"
          openOnFocus={true}
          noOptionsText="No Banks Found.Please add a new one."
          name="bankName"
          onFocus={() => {
            setCurrentComponent("add_distributor_bank");
          }}
          disableClearable={false}
          onChange={handleBankSelection}
          isOptionEqualToValue={(option, value) => {
            return option.id === value.id;
          }}
          getOptionLabel={(option) => {
            if (typeof option === "string") {
              return option;
            }
            if (typeof option === "object" && option !== null) {
              return `${option?.bankName || ""} - ${
                option?.accountNo || ""
              }`.trim();
            }
            return "";
          }}
          options={bankList}
          value={localFormState?.bankName || null}
          markers={JSON.stringify({
            up: "add_distributor_payee_name",
            left: "add_distributor_payee_name",
            right: "add_distributor_payment_type",
            down: "bank_filter_bank_code",
          })}
        />
      </Grid>
      <Grid item xs={2}>
        <NumberInput
          id="add_distributor_account_no"
          name="accountNumber"
          ref={addDistributorAccountNoRef}
          label="Account Number"
          onFocus={() => setCurrentComponent("add_distributor_account_no")}
          onValueChange={(values) => {
            setLocalFormState((prev) => ({
              ...prev,
              accountNumber: values.formattedValue,
            }));
            handleFormChange("accountNumber", values.formattedValue);
          }}
          value={localFormState?.accountNumber}
          decimalScale={0}
          allowNegative={false}
          allowLeadingZeros={true}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_payee_name",
              down: "add_distributor_payment_type",
              left: "add_distributor_payee_name",
              right: "add_distributor_payment_type",
            }),
          }}
        />
      </Grid>
      <Grid item xs={2}>
        <TextInput
          id="add_distributor_ifsc"
          name="ifsc"
          ref={addDistributorIfscRef}
          label="IFSC CODE"
          onFocus={() => setCurrentComponent("add_distributor_ifsc")}
          // onValueChange={(e) => {
          //   console.log("e", e);
          //   setLocalFormState((prev) => ({
          //     ...prev,
          //     ifsc: e.formattedValue,
          //   }));
          //   handleFormChange("ifsc", e.formattedValue);
          // }}
          onChange={(e) => {
            const ifsc = e.target.value;
            setLocalFormState((prev) => ({
              ...prev,
              ifsc: ifsc,
            }));
            handleFormChange("ifsc", ifsc);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "",
              down: "",
              left: "add_distributor_bank",
              right: "add_distributor_payment_type",
            }),
          }}
        />
      </Grid>
      <Grid item xs={2}>
        <TextInput
          id="add_distributor_micr"
          name="micrCode"
          ref={addDistributorMicrRef}
          label="MICR CODE"
          onFocus={() => setCurrentComponent("add_distributor_micr")}
          // onValueChange={(e) => {
          //   console.log("e", e);
          //   setLocalFormState((prev) => ({
          //     ...prev,
          //     micrCode: e.formattedValue,
          //   }));
          //   handleFormChange("micrCode", e.formattedValue);
          // }}
          onChange={(e) => {
            const micrCode = e.target.value;
            setLocalFormState((prev) => ({
              ...prev,
              micrCode: micrCode,
            }));
            handleFormChange("micrCode", micrCode);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "",
              down: "",
              left: "add_distributor_bank",
              right: "add_distributor_payment_type",
            }),
          }}
        />
      </Grid>
      {false && (
        <Grid item xs={2}>
          <NumberInput
            id="add_distributor_opening_balance"
            name="openingBalance"
            ref={addDistributorOpeningBalanceRef}
            label="Opening Balance"
            onFocus={() =>
              setCurrentComponent("add_distributor_opening_balance")
            }
            onValueChange={(values, e) => {
              setLocalFormState((prev) => {
                return { ...prev, openingBalance: values.floatValue };
              });
              handleFormChange("openingBalance", values.floatValue);
            }}
            value={localFormState?.openingBalance}
            decimalScale={0}
            allowNegative={false}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_distributor_payee_name",
                down: "",
                left: "add_distributor_ifsc",
                right: "add_distributor_payment_type",
              }),
            }}
          />
        </Grid>
      )}
      <Grid item xs={2}>
        <SelectInput
          id="add_distributor_payment_type"
          name="openingTransactionType"
          ref={addDistributorOpeningTransactionTypeRef}
          label="Type"
          onFocus={() => setCurrentComponent("add_distributor_payment_type")}
          onChange={(event, value) => {
            setOpeningTransactionType(value);
            handleFormChange("openingTransactionType", null, true, value);
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.label === value.label}
          options={openingTransactionTypes}
          value={openingTransactionType || null}
          markers={JSON.stringify({
            up: "add_distributor_bank",
            down: "add_distributor_payment_type",
            left: "add_distributor_bank",
            right: "add_distributor_credit_period",
          })}
        />
      </Grid>
      {false && (
        <Grid item xs={2}>
          <NumberInput
            id="add_distributor_credit_amount"
            name="creditAmount"
            getInputRef={addDistributorCreditAmountRef}
            label="Credit Amount"
            onFocus={() => setCurrentComponent("add_distributor_credit_amount")}
            value={localFormState?.creditAmount}
            decimalScale={0}
            allowNegative={false}
            onValueChange={(values, e) => {
              setLocalFormState((prev) => {
                return { ...prev, creditAmount: values.floatValue };
              });
              handleFormChange("creditAmount", values.floatValue);
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "add_distributor_account_no",
                down: "",
                left: "add_distributor_payment_type",
                right: "add_distributor_credit_period",
              }),
            }}
          />
        </Grid>
      )}
      <Grid item xs={2}>
        <NumberInput
          id="add_distributor_credit_period"
          name="creditPeriod"
          getInputRef={addDistributorCreditPeriodRef}
          label="Credit Period"
          onFocus={() => setCurrentComponent("add_distributor_credit_period")}
          value={localFormState?.creditPeriod}
          decimalScale={0}
          allowNegative={false}
          onValueChange={(values, e) => {
            setLocalFormState((prev) => {
              return { ...prev, creditPeriod: values.floatValue };
            });
            handleFormChange("creditPeriod", values.floatValue);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_payee_name",
              down: "add_distributor_payee_name",
              left: "add_distributor_payment_type",
              right: "add_distributor_tcs_applied",
            }),
          }}
        />
      </Grid>
      <Grid item xs={2}>
        <Switch
          id="add_distributor_tcs_applied"
          name="appliedTcs"
          ref={addDistributorTcsPercentRef}
          label="Apply TCS"
          checked={(formState && formState.appliedTcs) || false}
          onFocus={() => setCurrentComponent("add_distributor_tcs_applied")}
          onChange={(e) => {
            handleFormChange("appliedTcs", e.target.checked);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_account_no",
              down: "add_distributor_payee_name",
              left: "add_distributor_credit_period",
              right: "add_distributor_payable_from_admin",
            }),
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <Switch
          id="add_distributor_payable_from_admin"
          name="payableFromAdmin"
          ref={addDistributorPayableFromAdminRef}
          label="Payable from Admin"
          checked={(formState && formState.payableFromAdmin) || false}
          onFocus={() =>
            setCurrentComponent("add_distributor_payable_from_admin")
          }
          onChange={(e) => {
            handleFormChange("payableFromAdmin", e.target.checked);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_account_no",
              down: "add_distributor_payee_name",
              left: "add_distributor_tcs_applied",
              right: "add_distributor_chq_print_enable",
            }),
          }}
        />
      </Grid>
      <Grid item xs={3}>
        <Switch
          id="add_distributor_chq_print_enable"
          name="chqPrintEnable"
          ref={addDistributorChqPrintEnableRef}
          label="Chq. Print Enable"
          checked={(formState && formState.chqPrintEnable) || false}
          onFocus={() =>
            setCurrentComponent("add_distributor_chq_print_enable")
          }
          onChange={(e) => {
            handleFormChange("chqPrintEnable", e.target.checked);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_account_no",
              down: "add_distributor_payee_name",
              left: "add_distributor_payable_from_admin",
              right: "add_distributor_pan_tan",
            }),
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <TextInput
          id="add_distributor_pan_tan"
          name="panTan"
          ref={addDistributorPanTanRef}
          label="PAN/TAN"
          value={localFormState?.panTan || ""}
          onFocus={() => setCurrentComponent("add_distributor_pan_tan")}
          onChange={(e) => {
            const upperValue = e.target.value.toUpperCase();
            setLocalFormState((prev) => ({
              ...prev,
              panTan: upperValue,
            }));
            handleFormChange("panTan", upperValue);
          }}
          inputProps={{
            "data-markers": JSON.stringify({
              up: "add_distributor_cstvat_number",
              down: "add_distributor_type",
              left: "add_distributor_chq_print_enable",
              right: "add_distributor_payee_name",
            }),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default AccountTab;
